<script setup lang="ts">
const audio = ref<HTMLAudioElement>()
const route = useRoute()
const { isSoundEnabled, initializeSoundPreference } = useSound()
const isAdventureMode = computed(
  () => route.path === '/adventure' || route.path.startsWith('/adventure/'),
)
const isAdventureGame = computed(() =>
  /^\/adventure\/[^/]+\/game\/[^/]+\/?$/.test(route.path),
)
const isStoryNarrationActive = useState('story-narration-active', () => false)
const musicVolume = computed(() =>
  isAdventureGame.value || isStoryNarrationActive.value ? 0.05 : 0.28,
)

function removeInteractionListeners() {
  document.removeEventListener('pointerdown', startAfterInteraction)
  document.removeEventListener('keydown', startAfterInteraction)
}

async function playMusic() {
  if (!audio.value || !isSoundEnabled.value || !isAdventureMode.value) return

  audio.value.volume = musicVolume.value

  try {
    await audio.value.play()
    removeInteractionListeners()
  } catch {
    // Browsers can block autoplay until the learner interacts with the page.
  }
}

function startAfterInteraction(event: Event) {
  const target = event.target
  if (
    target instanceof Element &&
    target.closest('[data-adventure-music-toggle]')
  ) {
    return
  }

  void playMusic()
}

onMounted(() => {
  initializeSoundPreference()

  document.addEventListener('pointerdown', startAfterInteraction)
  document.addEventListener('keydown', startAfterInteraction)
  void playMusic()
})

watch(
  [isSoundEnabled, isAdventureMode, musicVolume],
  ([enabled, adventureMode, volume]) => {
    if (audio.value) audio.value.volume = volume

    if (enabled && adventureMode) void playMusic()
    else audio.value?.pause()
  },
  { flush: 'post' },
)

onUnmounted(() => {
  removeInteractionListeners()
  audio.value?.pause()
})
</script>

<template>
  <audio
    ref="audio"
    :src="isAdventureMode ? '/audio/adventure/royal-oak-path.mp3' : undefined"
    preload="none"
    loop
  />
</template>
